import { Check, Plus, AlertTriangle, AlertCircle } from "lucide-react";
import { Tonal } from "@/components/ui/tonal";
import { cn } from "@/lib/utils";
import type { JourneyFilter, SelectedStepComparison } from "./comparison-types";
import { JourneyStepCard } from "./journey-step-card";

export interface JourneyStepFinding {
  id: string;
  type: string;
  severity?: string | null;
  title?: string | null;
  ui_fact?: string | null;
}

export interface AlignedJourneyStep {
  index: number;
  status: string;
  summary: string;
  baselineStep: {
    step_number: number;
    action: string;
    route?: string | null;
    note?: string | null;
  } | null;
  currentStep: {
    step_number: number;
    action: string;
    route?: string | null;
    note?: string | null;
  } | null;
  baselineFindings: JourneyStepFinding[];
  currentFindings: JourneyStepFinding[];
}

export interface JourneyDiffData {
  stats: {
    totalAlignedSteps: number;
    same: number;
    resolved: number;
    newPath: number;
    persists: number;
    regressions: number;
    bypassed: number;
    stalledInBaseline: number;
  };
  steps: AlignedJourneyStep[];
}

export interface JourneyTraceDiffProps {
  journeyDiff: JourneyDiffData;
  filteredSteps: AlignedJourneyStep[];
  journeyFilter: JourneyFilter;
  setJourneyFilter: (filter: JourneyFilter) => void;
  totalJourneyDeltas: number;
  baseRunNumber?: number;
  currentRunNumber?: number;
  activeBaseRunId: string | null;
  currentWalkthroughId: string;
  onOpenFinding: (id: string) => void;
  onSelectStepComparison: (step: SelectedStepComparison) => void;
}

export function JourneyTraceDiff({
  journeyDiff,
  filteredSteps,
  journeyFilter,
  setJourneyFilter,
  totalJourneyDeltas,
  baseRunNumber,
  currentRunNumber,
  activeBaseRunId,
  currentWalkthroughId,
  onOpenFinding,
  onSelectStepComparison,
}: JourneyTraceDiffProps) {
  return (
    <div className="space-y-4">
      {/* Journey Diff Filter Strip */}
      <div className="flex items-center justify-between gap-2 border-b border-border/70 pb-2 flex-wrap">
        <div className="flex items-center gap-1.5 flex-wrap">
          <button
            type="button"
            onClick={() => setJourneyFilter("all")}
            className={cn(
              "flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-xs font-medium transition-colors cursor-pointer border",
              journeyFilter === "all"
                ? "bg-muted text-foreground font-semibold border-border"
                : "border-transparent text-muted-foreground hover:bg-muted/60 hover:text-foreground",
            )}
          >
            <span>All steps</span>
            <span className="font-mono text-xs tabular-nums">
              {journeyDiff.stats.totalAlignedSteps}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setJourneyFilter("deltas")}
            className={cn(
              "flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-xs font-medium transition-colors cursor-pointer border",
              journeyFilter === "deltas"
                ? "bg-muted text-foreground font-semibold border-border"
                : "border-transparent text-muted-foreground hover:bg-muted/60 hover:text-foreground",
            )}
          >
            <span>Deltas only</span>
            <span className="font-mono text-xs tabular-nums">
              {totalJourneyDeltas}
            </span>
          </button>

          {journeyDiff.stats.resolved > 0 && (
            <button
              type="button"
              onClick={() => setJourneyFilter("resolved")}
              className={cn(
                "flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors cursor-pointer",
                journeyFilter === "resolved"
                  ? "bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 font-semibold border border-emerald-500/40"
                  : "text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/10",
              )}
            >
              <Check className="size-3" />
              <span>Resolved ({journeyDiff.stats.resolved})</span>
            </button>
          )}

          {journeyDiff.stats.newPath > 0 && (
            <button
              type="button"
              onClick={() => setJourneyFilter("new_path")}
              className={cn(
                "flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors cursor-pointer",
                journeyFilter === "new_path"
                  ? "bg-sky-500/20 text-sky-700 dark:text-sky-300 font-semibold border border-sky-500/40"
                  : "text-sky-700 dark:text-sky-400 hover:bg-sky-500/10",
              )}
            >
              <Plus className="size-3" />
              <span>New path ({journeyDiff.stats.newPath})</span>
            </button>
          )}

          {journeyDiff.stats.regressions > 0 && (
            <button
              type="button"
              onClick={() => setJourneyFilter("regression")}
              className={cn(
                "flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors cursor-pointer",
                journeyFilter === "regression"
                  ? "bg-destructive/20 text-destructive font-semibold border border-destructive/40"
                  : "text-destructive hover:bg-destructive/10",
              )}
            >
              <AlertTriangle className="size-3" />
              <span>Regressions ({journeyDiff.stats.regressions})</span>
            </button>
          )}

          {journeyDiff.stats.persists > 0 && (
            <button
              type="button"
              onClick={() => setJourneyFilter("persists")}
              className={cn(
                "flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors cursor-pointer",
                journeyFilter === "persists"
                  ? "bg-amber-500/20 text-amber-700 dark:text-amber-300 font-semibold border border-amber-500/40"
                  : "text-amber-700 dark:text-amber-400 hover:bg-amber-500/10",
              )}
            >
              <AlertCircle className="size-3" />
              <span>Persists ({journeyDiff.stats.persists})</span>
            </button>
          )}

          {journeyDiff.stats.bypassed > 0 && (
            <button
              type="button"
              onClick={() => setJourneyFilter("bypassed")}
              className={cn(
                "flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors cursor-pointer",
                journeyFilter === "bypassed"
                  ? "bg-muted text-foreground font-semibold border border-border"
                  : "text-muted-foreground hover:bg-muted/60",
              )}
            >
              <span>Bypassed ({journeyDiff.stats.bypassed})</span>
            </button>
          )}
        </div>
      </div>

      {/* Journey Step Rows */}
      <div className="divide-y divide-border/70">
        {filteredSteps.length === 0 ? (
          <Tonal
            padding="md"
            className="text-center rounded-lg border border-border/80 space-y-1 py-8"
          >
            <p className="text-sm font-medium text-foreground">
              No steps match filter "{journeyFilter}".
            </p>
            <p className="text-xs text-muted-foreground">
              Try switching to "All steps" or "Deltas only".
            </p>
          </Tonal>
        ) : (
          filteredSteps.map((step) => (
            <JourneyStepCard
              key={step.index}
              step={step}
              baseRunNumber={baseRunNumber}
              currentRunNumber={currentRunNumber}
              activeBaseRunId={activeBaseRunId}
              currentWalkthroughId={currentWalkthroughId}
              onOpenFinding={onOpenFinding}
              onSelectStepComparison={onSelectStepComparison}
            />
          ))
        )}
      </div>
    </div>
  );
}
